import React from "react";
import FileIcon from "components/common/FileIcon/FileIcon";
import {
  downloadFileFromServer,
  extractFileNameFromS3URL,
  getFileExtension,
  sanitizeDocumentUrl,
} from "Utils/utils";
import {
  openAttachmentPreviewInNewTab,
  openDocumentPreviewInNewTab,
} from "Utils/attachmentPreview";
import { HiDownload, HiEye } from "react-icons/hi";
import * as FileSaver from "file-saver";
import { normalizeCosCaseNoteAttachments } from "./cosCaseNoteDisplayUtils";

const CosCaseNoteAttachments = ({ note }) => {
  const items = normalizeCosCaseNoteAttachments(note);
  const noteUuid = note?.uuid;

  if (items.length === 0) return null;

  const handleView = (item, fileName) => {
    if (item?.attachment_url) {
      openDocumentPreviewInNewTab(item.attachment_url, {
        preferredFileName: fileName,
        isFullUrl: true,
      });
      return;
    }
    if (item?.attachments) {
      openAttachmentPreviewInNewTab(item.attachments, fileName);
    }
  };

  const handleDownload = async (item, fileName) => {
    const displayName = fileName || "download";
    try {
      if (item?.attachment_url) {
        const response = await fetch(sanitizeDocumentUrl(item.attachment_url));
        if (!response.ok) throw new Error("Download failed");
        const blob = await response.blob();
        FileSaver.saveAs(blob, displayName);
        return;
      }
      if (item?.attachments) {
        await downloadFileFromServer(item.attachments, displayName);
      }
    } catch (error) {
      console.error("Attachment download failed:", error);
    }
  };

  return (
    <div className='mt-2 pt-2 border-t border-gray-100'>
      <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5'>
        Attachments ({items.length})
      </p>
      <div className='w-full min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-1.5'>
        {items.map((item, index) => {
          const fileName = item?.original_name;
          const storagePath = item?.attachments;
          const actualName =
            (fileName && String(fileName)) ||
            extractFileNameFromS3URL(storagePath || item?.attachment_url) ||
            "";
          const displayName = actualName || `Attachment ${index + 1}`;
          const rowKey =
            item?.id != null ? `att-${item.id}` : `${noteUuid}-att-${index}`;

          return (
            <div
              key={rowKey}
              className='min-w-0 max-w-full flex items-center gap-1.5 border border-gray-200 rounded-md px-2 py-1 text-xs bg-white'
            >
              <div className='flex-shrink-0 bg-blue-50 rounded-full h-7 w-7 flex items-center justify-center'>
                {FileIcon(
                  getFileExtension(
                    fileName
                      ? fileName.toLowerCase()
                      : extractFileNameFromS3URL(
                          storagePath || item?.attachment_url
                        )?.toLowerCase()
                  ),
                  11
                )}
              </div>
              <span
                className='min-w-0 flex-1 font-medium text-gray-900 truncate text-left'
                title={displayName}
              >
                {displayName}
              </span>
              <div className='flex flex-shrink-0 items-center gap-0.5'>
                <button
                  type='button'
                  onClick={() => handleView(item, fileName)}
                  className='p-1 rounded hover:bg-blue-50 cursor-pointer'
                  title={`View — ${displayName}`}
                >
                  <HiEye className='w-3.5 h-3.5 text-blue-600' />
                </button>
                <button
                  type='button'
                  onClick={() => handleDownload(item, fileName)}
                  className='p-1 rounded hover:bg-gray-100 cursor-pointer'
                  title={`Download — ${displayName}`}
                >
                  <HiDownload className='w-3.5 h-3.5 text-gray-600' />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CosCaseNoteAttachments;
